import { FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import { useField, UseFieldConfig } from 'react-final-form'
import { Rack, RackButton, RackOption } from '@cargomart/ui'
import { Clusterer, GeolocationControl, Map as YmapsMap, Placemark, SearchControl } from '@pbe/react-yandex-maps'
import yarchePlacemarkIconPath from 'assets/yarche-placemark.svg'
import yarcheSelectedTerminalIconPath from 'assets/yarche-selected-terminal.svg'
import classNames from 'classnames'
import { FieldFormItem } from 'forms'
import { MODAL_IDS, ModalItem, ModalSimple, useModal } from 'src/components'
import { LinkButton } from 'src/components/Link'
import { TerminalItem } from 'src/schema'

import { useGetAllYarcheTerminalsQuery } from '../../api'
import { ModalTerminal } from '../ModalTerminal'
import { RouteSideBar } from './RouteSideBar'
import { SideBarMode } from './types'
import { useYarcheAddressSelect } from './useYarcheAddressSelect'
import { calculateDistance, filterCoordinates } from './utils'

type OrderYarcheGuestCreateFormRouteProps = {
  mode: 'departurePoint' | 'destinationPoint'
} & Pick<UseFieldConfig<{ terminalId: TerminalItem['id'] | undefined }>, 'validate'>

const OrderYarcheGuestCreateFormRoute: FC<OrderYarcheGuestCreateFormRouteProps> = memo(({ mode, validate }) => {
  const selectTerminalModalId = useMemo(
    () => (mode === 'departurePoint' ? MODAL_IDS.YarcheDepartureSelectMap : MODAL_IDS.YarcheDestionationSelectMap),
    [mode]
  )
  const terminalModalId = useMemo(
    () => (mode === 'departurePoint' ? MODAL_IDS.YarcheDepartureTerminal : MODAL_IDS.YarcheDestionationTerminal),
    [mode]
  )
  const { openModal, closeModal } = useModal()

  const {
    input: { value: terminal, onChange: changeTerminalId }
  } = useField<{ terminalId: TerminalItem['id'] | undefined }>(mode, { validate })
  const { data: rawTerminals } = useGetAllYarcheTerminalsQuery('')
  const terminals = useMemo(() => rawTerminals || [], [rawTerminals])

  const {
    sideBarMode,
    setSideBarMode,
    removeSelectedItems,
    handleCloseMapModal,
    handleChangeConfirmedTerminal,
    selectedTerminal,
    handleChangeSelectedTerminal,
    zoom,
    center,
    terminalByField
  } = useYarcheAddressSelect({
    terminals,
    terminalId: terminal.terminalId,
    changeTerminalId,
    modalId: selectTerminalModalId
  })

  const [load, setLoad] = useState(false)
  const [closestTerminals, setClosestTerminals] = useState(terminals || [])
  const [closestFilterInfo, setClosestFilterInfo] = useState<{
    center: number[]
    radius: number
  } | null>(null)

  const mapRef = useRef<ymaps.Map>()

  const filterTerminalByDistance = () => {
    const center = mapRef.current!.getCenter()
    const bounds = mapRef.current!.getBounds()
    const northEast = bounds[1]

    const radius = calculateDistance(
      { latitude: center[0], longitude: center[1] },
      { latitude: northEast[0], longitude: northEast[1] }
    )
    setClosestFilterInfo({
      radius,
      center
    })
  }

  const handleOpenTerminalModal = () => {
    terminal.terminalId && handleChangeSelectedTerminal(terminal.terminalId, false)
    openModal(selectTerminalModalId)
  }

  useEffect(() => {
    mapRef.current?.setCenter(center)
  }, [center])

  useEffect(() => {
    mapRef.current?.setZoom(zoom)
  }, [zoom])

  useEffect(() => {
    if (!closestFilterInfo) {
      setClosestTerminals(terminals)

      return
    }
    const filtered = filterCoordinates<TerminalItem>(
      terminals,
      { latitude: closestFilterInfo.center[0], longitude: closestFilterInfo.center[1] },
      closestFilterInfo.radius
    )
    setClosestTerminals(filtered)
  }, [terminals, closestFilterInfo])

  useEffect(() => {
    if (!load) return
    if (!mapRef.current) return
    mapRef.current.events.add('boundschange', filterTerminalByDistance)
  }, [load])

  useEffect(() => {
    if (!load) return
    if (!mapRef.current) return
    filterTerminalByDistance()
  }, [center, load])

  return (
    <>
      <FieldFormItem name={mode} validateError={(error) => error}>
        <Rack<TerminalItem['id']>
          onChange={changeTerminalId}
          value={terminal.terminalId}
          label={
            terminal.terminalId ? 'Магазин «Ярче»' : <span className="text-on-surface-variant">Магазин не выбран</span>
          }
        >
          {terminalByField && (
            <RackOption
              key={terminalByField.id}
              isDelete
              withKnobs
              title={terminalByField.name}
              value={terminal.terminalId}
              description={
                <div className="flex flex-col gap-2">
                  {terminalByField.address}
                  <LinkButton className="focus:ring-[none]" onClick={() => openModal(terminalModalId)}>
                    Показать подробно на карте
                  </LinkButton>
                </div>
              }
            />
          )}
          <RackButton label={terminal.terminalId ? 'Изменить' : 'Выбрать'} onClick={handleOpenTerminalModal} />
        </Rack>
        <ModalItem id={selectTerminalModalId}>
          <ModalSimple
            bodyClassName="!px-0 flex flex-col max-md:!py-0"
            hasHeader={false}
            id={selectTerminalModalId}
            isCloseButton={false}
            size="xl"
            onClose={() => {
              if (sideBarMode === 'detail') {
                removeSelectedItems()
                setSideBarMode('list')

                return
              }
              setLoad(false)
              handleCloseMapModal()
              setClosestFilterInfo(null)
            }}
          >
            <SelectedTerminalModal
              center={center}
              closestTerminals={closestTerminals}
              handleChangeConfirmedTerminal={handleChangeConfirmedTerminal}
              handleChangeSelectedTerminal={handleChangeSelectedTerminal}
              mapRef={mapRef}
              selectedTerminal={selectedTerminal}
              setLoad={setLoad}
              sideBarMode={sideBarMode}
              terminals={terminals}
              zoom={zoom}
              onCloseSidebar={() => {
                if (sideBarMode === 'detail') {
                  removeSelectedItems()
                  setSideBarMode('list')

                  return
                }
                setLoad(false)
                handleCloseMapModal()
                setClosestFilterInfo(null)
              }}
            />
          </ModalSimple>
        </ModalItem>
        <ModalItem id={terminalModalId}>
          <ModalSimple
            id={terminalModalId}
            onClose={() => closeModal(terminalModalId)}
            title={terminalByField?.name || ''}
          >
            {terminalByField && <ModalTerminal terminal={terminalByField} />}
          </ModalSimple>
        </ModalItem>
      </FieldFormItem>
    </>
  )
})

type SelectedTerminalModalProps = {
  center: number[]
  closestTerminals: TerminalItem[]
  handleChangeConfirmedTerminal: () => void
  handleChangeSelectedTerminal: (option: TerminalItem['id'] | TerminalItem | null, changeMode?: boolean) => void
  mapRef: React.MutableRefObject<ymaps.Map | undefined>
  onCloseSidebar: () => void
  selectedTerminal: TerminalItem | null
  setLoad: React.Dispatch<React.SetStateAction<boolean>>
  sideBarMode: SideBarMode
  terminals: TerminalItem[]
  zoom: number
}
const SelectedTerminalModal: FC<SelectedTerminalModalProps> = ({
  terminals,
  selectedTerminal,
  mapRef,
  setLoad,
  center,
  zoom,
  closestTerminals,
  handleChangeConfirmedTerminal,
  handleChangeSelectedTerminal,
  sideBarMode,
  onCloseSidebar
}) => {
  useGetAllYarcheTerminalsQuery('')

  return (
    <YmapsMap
      instanceRef={mapRef}
      onLoad={() => setLoad(true)}
      style={{ width: '100%', height: '100%' }}
      className={classNames(
        'relative !h-[80svh] overflow-hidden rounded-xl max-md:!h-full max-md:!max-w-full max-md:flex-grow'
      )}
      defaultState={{
        center,
        zoom
      }}
    >
      <SearchControl
        options={{
          noPlacemark: true
        }}
      />
      <GeolocationControl />
      {selectedTerminal && (
        <Placemark
          geometry={[Number(selectedTerminal.latitude), Number(selectedTerminal.longitude)]}
          options={{
            iconLayout: 'default#image',
            iconImageSize: [56, 56],
            iconImageOffset: [-28, -28],
            iconImageHref: yarcheSelectedTerminalIconPath
          }}
        />
      )}

      <Clusterer
        options={{
          preset: 'islands#darkGreenClusterIcons',
          groupByCoordinates: false,
          clusterHideIconOnBalloonOpen: false,
          geoObjectHideIconOnBalloonOpen: false,
          gridSize: 80
        }}
      >
        {terminals &&
          terminals.map((item) => {
            const isSelected = item.id === selectedTerminal?.id

            return (
              <Placemark
                key={item.id}
                geometry={[Number(item.latitude), Number(item.longitude)]}
                onClick={() => handleChangeSelectedTerminal(item)}
                options={{
                  iconLayout: 'default#image',
                  iconImageSize: isSelected ? [56, 56] : [32, 32],
                  iconImageOffset: isSelected ? [-28, -28] : [-16, -16],
                  iconImageHref: isSelected ? yarcheSelectedTerminalIconPath : yarchePlacemarkIconPath
                }}
              />
            )
          })}
      </Clusterer>
      <RouteSideBar
        closeSideBar={onCloseSidebar}
        closestTerminals={closestTerminals}
        handleChangeConfirmedTerminal={handleChangeConfirmedTerminal}
        handleChangeSelectedTerminal={handleChangeSelectedTerminal}
        mode={sideBarMode}
        selectedTerminal={selectedTerminal}
      />
    </YmapsMap>
  )
}
export default OrderYarcheGuestCreateFormRoute
